type PricingPolicy = {
  type: 'fixed' | 'recurring';
  adjustmentType: 'FIXED_AMOUNT' | 'PERCENTAGE' | 'PRICE';
  adjustmentValue: number;
  afterCycle?: number;
};

export function applyFirstFixedPolicyDiscount(
  unitAmountMinor: number,
  pricingPolicies: PricingPolicy[],
): number {
  const fixedPolicy = pricingPolicies.find((p) => p.type === 'fixed');
  if (!fixedPolicy) return unitAmountMinor;

  switch (fixedPolicy.adjustmentType) {
    case 'PERCENTAGE': {
      // Grouped as the checkout does it — Dinero's `percentage()` is `multiply(value / 100)`.
      // The other association (`amount * value / 100`) lands a cent off on some fractional
      // percentages, which would print a figure the customer is never charged.
      const discount = Math.round(unitAmountMinor * (fixedPolicy.adjustmentValue / 100));
      return Math.max(0, unitAmountMinor - discount);
    }
    case 'FIXED_AMOUNT':
      return Math.max(0, unitAmountMinor - fixedPolicy.adjustmentValue);
    case 'PRICE':
      return Math.max(0, fixedPolicy.adjustmentValue);
    default:
      return unitAmountMinor;
  }
}

/**
 * What a Shopify selling-plan price actually charges on the first order.
 *
 * The stored amount is not the amount charged: at checkout the plan's own `fixed` policy is
 * applied on top of it by `sellingPlansAdjuster` (repo root `src/lib/adjuster/selling-plans.ts`,
 * which this app cannot import). Returns `null` when no fixed policy applies, so callers can tell
 * "charges the stored amount" from "charges something else".
 */
export function sellingPlanFirstChargeAmount(
  unitAmountMinor: number,
  priceSettings: unknown,
): number | null {
  const pricingPolicies = (priceSettings as { providerSpecific?: Record<string, { pricingPolicies?: PricingPolicy[] }> } | null | undefined)
    ?.providerSpecific?.shopify?.pricingPolicies;

  // The caller's value is `unknown` by the time it reaches here: it travels through the price
  // form's `z.array(z.any())` schema, so nothing has checked its shape.
  if (!Array.isArray(pricingPolicies)) return null;
  if (!pricingPolicies.some((p) => p?.type === 'fixed')) return null;

  return applyFirstFixedPolicyDiscount(unitAmountMinor, pricingPolicies);
}

export function computeSubscriptionDisplayAmount(params: {
  unitAmountMinor: number;
  quantity: number;
  pricingPolicies?: PricingPolicy[] | null;
}): number {
  const { unitAmountMinor, quantity, pricingPolicies } = params;

  let effectiveUnitPrice = unitAmountMinor;
  if (pricingPolicies && pricingPolicies.length > 0) {
    effectiveUnitPrice = applyFirstFixedPolicyDiscount(unitAmountMinor, pricingPolicies);
  }

  return effectiveUnitPrice * quantity;
}
